/**
 * LlmProfilesEditor — P7 of `ai-docs/llm-resolver-unification-impl-plan.md`.
 *
 * The platform's `PlatformLlmProfiles` catalog editor (read + validate-then-
 * save the whole bundle atomically). Replaces the profile-definition half
 * of the deleted `<DefaultLlmChoiceEditor>`.
 *
 * MVP shape (P7 first cut):
 *   - Loads the current catalog via GET /api/platform/settings/llm-profiles.
 *   - 🚨 WHEN THAT RETURNS null the platform has NO catalog and every LLM
 *     resolution is failing `PLATFORM_CATALOG_ABSENT` fleet-wide. That is not a
 *     first-run nicety — it happened for ~4 minutes on 2026-08-27 — and THIS
 *     PAGE is where an operator comes to fix it. Two distinct recoveries, and
 *     the page must not conflate them:
 *       · `GET .../previous` non-null → offer RESTORE first. It is THEIR
 *         catalog. An undo, one generation deep.
 *       · `previous` null (never overwritten — a genuinely virgin platform) →
 *         offer `GET .../starter`, LABELLED AS A TEMPLATE.
 *     ⚖ The primary defect this replaced was not the failing save; it was that
 *     the old hardcoded `STARTER_CATALOG` was rendered with NO cue it was a
 *     template, so a template read as current state. A 422 is a wall an
 *     operator hits and understands; an unlabelled template is a wrong answer
 *     they cannot detect.
 *   - Renders a JSON textarea pre-populated with the catalog. The operator
 *     edits the JSON directly — the picker-driven Pinned/Criteria tabbed UI
 *     described in the plan (`<ProviderPicker>`/`<ModelPicker>` + per-field
 *     criteria editing) is a follow-up enhancement; the JSON path
 *     unblocks the wire-level surface today.
 *   - Validate-then-save via POST. The 422 envelope's
 *     `error.missingProviders[]` (pre-flight) is parsed and rendered inline
 *     in the save toast, so a refused save NAMES the providers to seed.
 *     ⚠ This block used to also claim `error.validations[]` (per-profile) was
 *     rendered. It never was, and lexserver never sent it — the string appears
 *     nowhere in `lexserver/src/routes/**` (measured 2026-08-25). A docblock
 *     describing a field NEITHER SIDE implements is worse than silence: the
 *     next reader looks for the consumer, does not find it, and adds one for a
 *     producer that does not exist. Corrected rather than deleted so the
 *     absence is deliberate and stays that way.
 *   - A separate Default-Chat-Profile dropdown rewires the catalog's
 *     `defaultChatProfile` pointer (cheap server-side validate — no LLM call).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  type CatalogAdvisorySet,
  type PlatformLlmProfiles,
  type PlatformLlmProfilesMasked,
  type PlatformLlmProfilesPrevious,
  type PlatformLlmProfilesStarter,
  PlatformApiError,
  starterCatalogOnly,
} from "@/lib/platform-api";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import {
  advisoryEmphasis,
  ifMatchToken,
  isNoOpSave,
  selectAdvisoryState,
} from "@/lib/catalog-advisory-state";

interface SaveToast {
  ok: boolean;
  text: string;
  missingProviders?: string[];
  /** Present only on a 412 — who holds the server's version, and since when. */
  conflict?: { storedSetBy: string | null; storedSetAt: string | null };
}

export function LlmProfilesEditor() {
  const api = usePlatformApi();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [current, setCurrent] = useState<PlatformLlmProfilesMasked | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [defaultBusy, setDefaultBusy] = useState(false);
  const [toast, setToast] = useState<SaveToast | null>(null);
  /** Only populated when the platform has NO catalog — see the docblock. */
  const [previous, setPrevious] = useState<PlatformLlmProfilesPrevious | null>(null);
  const [starter, setStarter] = useState<PlatformLlmProfilesStarter | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  /** Which recovery source(s) could not be READ — distinct from a source that
   *  read fine and reported nothing to offer. */
  const [recoveryReadError, setRecoveryReadError] = useState<string | null>(null);
  /** Observations about the STORED catalog. `null` = unread or read failed —
   *  which is NOT the same as "no advisories" and does not render as one. */
  const [advisories, setAdvisories] = useState<CatalogAdvisorySet | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getPlatformLlmProfiles();
      setCurrent(res.data);

      if (res.data) {
        // Normal path: a catalog exists. `res.data` is the MASKED superset
        // (setBy/setAt/lastValidated*), which `POST /`'s .strict() schema
        // refuses — narrow before it reaches the textarea.
        setPrevious(null);
        setStarter(null);
        setRecoveryReadError(null);
        setDraft(JSON.stringify(starterCatalogOnly(res.data), null, 2));

        // Advisories annotate a catalog that EXISTS, so they are read only on
        // this branch. Settled separately and swallowed on failure: the editor
        // is the page's primary function and a secondary annotation must never
        // be able to take it down. A failed read leaves `null`, which renders
        // as "could not read" — never as a clean catalog.
        void api
          .getPlatformLlmProfilesAdvisories()
          .then((a) => setAdvisories(a.data))
          .catch(() => setAdvisories(null));
        return;
      }

      // Catalog ABSENT: do not read advisories at all. The red banner below
      // explains this state more completely than `status:"unknown"` would and
      // carries the recovery action; a second quieter telling competes with it.
      // ⚖ Endorsed by lexide-im in the handoff as NOT a violation of
      // unknown-is-not-empty — see catalog-advisory-state.ts for the full
      // reasoning, and do not "restore" this as a missing case.
      setAdvisories(null);

      // NO CATALOG. Fetch both recovery sources so the page can tell the
      // operator which situation they are actually in before offering an
      // action. `previous` decides it: a real undo beats a template.
      //
      // ⚠ Deliberately NOT falling back to a hardcoded shape if these fail.
      // A page that invents a catalog during a fleet-wide outage is the
      // failure mode this rewrite exists to remove — surfacing what failed
      // is the honest answer (repo rule: no fallbacks).
      //
      // 🚨 SETTLED INDEPENDENTLY, not Promise.all. These two answer different
      // questions and only one of them is the recovery that matters. Failing
      // them together would let `/starter` — which has its own 500
      // (`STARTER_CATALOG_INVALID`, a defect in the resolver's built-ins) —
      // hide a perfectly good `/previous`, hiding the RESTORE button during
      // the outage it exists for. A read that failed must be representable as
      // "unknown", never collapse the read that succeeded.
      const [prevOut, starterOut] = await Promise.allSettled([
        api.getPlatformLlmProfilesPrevious(),
        api.getPlatformLlmProfilesStarter(),
      ]);

      const prev = prevOut.status === "fulfilled" ? prevOut.value.data : null;
      const starterData =
        starterOut.status === "fulfilled" ? starterOut.value.data : null;
      setPrevious(prev);
      setStarter(starterData);

      // Name what could not be read, so a missing affordance is never mistaken
      // for a missing OPTION. "No previous catalog" and "could not ask about a
      // previous catalog" are different facts and the operator must see which.
      const unreadable: string[] = [];
      if (prevOut.status === "rejected") {
        unreadable.push(
          `previous catalog (${prevOut.reason instanceof Error ? prevOut.reason.message : "read failed"})`,
        );
      }
      if (starterOut.status === "rejected") {
        unreadable.push(
          `starter template (${starterOut.reason instanceof Error ? starterOut.reason.message : "read failed"})`,
        );
      }
      setRecoveryReadError(unreadable.length > 0 ? unreadable.join("; ") : null);

      // Seed the textarea from whichever source we will actually offer, and
      // narrow BOTH: `/previous` nests its catalog, `/starter` spreads
      // advisory siblings flat. One helper, so neither path can forget.
      // If neither could be read, leave the box EMPTY rather than inventing
      // a catalog — an empty editor is honest, a fabricated one is not.
      const seed = prev ? prev.catalog : starterData;
      setDraft(seed ? JSON.stringify(starterCatalogOnly(seed), null, 2) : "");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const handleRestorePrevious = useCallback(async () => {
    setRestoreBusy(true);
    setToast(null);
    try {
      const res = await api.restorePlatformLlmProfilesPrevious();
      setCurrent(res.data);
      setPrevious(null);
      setStarter(null);
      setRecoveryReadError(null);
      setDraft(JSON.stringify(starterCatalogOnly(res.data), null, 2));
      setToast({ ok: true, text: "Previous catalog restored." });
    } catch (err) {
      // Server messages here are written to be read by an operator and
      // already name the repair — surface verbatim (see handleSave).
      const details = (err instanceof PlatformApiError ? err.details : undefined) as
        | { missingProviders?: string[] }
        | undefined;
      setToast({
        ok: false,
        text: err instanceof Error ? err.message : "Restore failed",
        missingProviders: details?.missingProviders,
      });
    } finally {
      setRestoreBusy(false);
    }
  }, [api]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  /** Dropdown options — read directly from the catalog JSON.
   *  Catalog STORAGE keys now accept both bare BASE names AND composite
   *  `<BASE>:<thinking>` keys (resolver schema loosened 2026-05-26).
   *  The dropdown is the single source of truth alongside the JSON
   *  editor — both render `Object.keys(catalog.profiles)`. To expose
   *  more options, the operator edits the JSON; the dropdown updates
   *  on the next save. */
  const profileNames = useMemo(() => {
    if (!current) return [];
    return Object.keys(current.profiles).sort();
  }, [current]);

  const handleSave = useCallback(async () => {
    let parsed: PlatformLlmProfiles;
    try {
      parsed = JSON.parse(draft);
    } catch (err) {
      setToast({ ok: false, text: `JSON parse failed: ${(err as Error).message}` });
      return;
    }
    // 🚨 Refuse a no-op save. Every write ROTATES `/previous`, and there is
    // exactly ONE generation of undo — so saving an unchanged draft destroys
    // the recovery slot while changing nothing. lexserver consumed a real
    // prior catalog exactly this way on 2026-08-31. The operator instinct this
    // protects is the reasonable one: clicking Save to be sure, during an
    // incident, on the page where the undo matters most.
    if (current && isNoOpSave(parsed, starterCatalogOnly(current))) {
      setToast({
        ok: true,
        text: "No changes to save — the catalog is already exactly this.",
      });
      return;
    }

    setBusy(true);
    setToast(null);
    try {
      // 🚨 Send the `setAt` THIS DRAFT WAS LOADED FROM as an `If-Match`
      // precondition (lexserver 7ca1d675d). Without it the save is
      // last-write-wins: the draft is seeded at mount, so a tab left open
      // across anyone else's save silently reverts their catalog — the hazard
      // that made this page unsafe to click during the 2026-08-31 prod
      // verification window.
      //
      // `current` is null only when no catalog is stored, in which case there
      // is nothing to be stale against and the header is correctly omitted.
      const res = await api.setPlatformLlmProfiles(parsed, ifMatchToken(current));
      setCurrent(res.data.saved);
      setPrevious(null);
      setStarter(null);
      setToast({
        ok: true,
        text: `Saved ${Object.keys(res.data.saved.profiles).length} profile(s).`,
      });
    } catch (err) {
      // 🚨 SURFACE THE SERVER'S MESSAGE VERBATIM — never paraphrase it into
      // local copy. lexserver's refusals name the repair AND the affordance
      // that would have predicted them, and this page cannot reproduce that:
      // the STORED catalog can be unsaveable for reasons invisible in the
      // draft on screen (a boot seed once wrote `anthropic` rows into storage
      // on an openai-codex-only platform, so a correct template with an honest
      // advisory still 422'd naming a provider shown nowhere here). Our words
      // can only describe the draft we can see. `missingProviders` rides
      // alongside as structured chrome, not as a replacement for the text.
      const details = (err instanceof PlatformApiError ? err.details : undefined) as
        | {
            missingProviders?: string[];
            storedSetAt?: string | null;
            storedSetBy?: string | null;
          }
        | undefined;

      // A 412 is NOT an ordinary failure — someone else's catalog is on the
      // server and this draft is stale. Refresh so the editor shows THEIR
      // version rather than leaving the operator's stale draft in the box
      // where a second click would look like a retry. Their edits are not
      // discarded silently: the toast names who changed it and when, so the
      // operator decides whether to re-apply.
      if (err instanceof PlatformApiError && err.code === "CATALOG_PRECONDITION_FAILED") {
        setToast({
          ok: false,
          text: err.message,
          conflict: {
            storedSetBy: details?.storedSetBy ?? null,
            storedSetAt: details?.storedSetAt ?? null,
          },
        });
        void refetch();
        return;
      }

      setToast({
        ok: false,
        text: err instanceof Error ? err.message : "Save failed",
        missingProviders: details?.missingProviders,
      });
    } finally {
      setBusy(false);
    }
  }, [api, draft, current, refetch]);

  const handleSetDefault = useCallback(
    async (name: string) => {
      setDefaultBusy(true);
      try {
        const res = await api.setPlatformDefaultChatProfile(name);
        setCurrent(res.data.saved);
        setToast({ ok: true, text: `Default chat profile rewired to "${name}".` });
      } catch (err) {
        setToast({
          ok: false,
          text: err instanceof Error ? err.message : "Update failed",
        });
      } finally {
        setDefaultBusy(false);
      }
    },
    [api],
  );

  return (
    <Card data-testid="llm-profiles-editor">
      <CardHeader>
        <CardTitle className="text-lg">LLM profile catalog (authoritative)</CardTitle>
        <CardDescription>
          The platform's <code>PlatformLlmProfiles</code> bundle — every org's{" "}
          <code>chatProfile</code> resolves against this catalog at runtime
          (lexruntime config-sync). Save is atomic and static:
          schema, missing-seedCredentials, and composite-name resolution
          are all checked before persistence; no LLM calls happen at save
          time (per-profile reachability is exercised at chat time +
          covered once per credential by the seed-credentials "Test" path).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading catalog…
          </div>
        ) : loadError ? (
          <div className="text-sm text-red-600 dark:text-red-400">{loadError}</div>
        ) : (
          <>
            {/* ── NO CATALOG ─────────────────────────────────────────────
                The whole point of this rewrite. Says plainly what state the
                platform is in, then offers the RIGHT recovery for it — never
                a template masquerading as current state. */}
            {!current && (
              <div
                className="rounded border border-red-400 bg-red-50 dark:bg-red-950 p-3 space-y-2"
                data-testid="llm-profiles-absent"
              >
                <div className="font-medium text-red-800 dark:text-red-300">
                  No catalog exists on this platform.
                </div>
                <p className="text-sm text-red-700 dark:text-red-400">
                  Every LLM resolution is failing with{" "}
                  <code>PLATFORM_CATALOG_ABSENT</code> until one is saved. The
                  JSON below is <strong>not your data</strong> — nothing is
                  stored yet.
                </p>

                {previous ? (
                  <div className="space-y-2">
                    <p className="text-sm text-red-700 dark:text-red-400">
                      A previous catalog is available. It was replaced{" "}
                      {new Date(previous.replacedAt).toLocaleString()} by{" "}
                      <code>{previous.replacedBy}</code> — restoring is an undo,
                      one generation deep, and is itself undoable.
                    </p>
                    <Button
                      onClick={handleRestorePrevious}
                      disabled={restoreBusy}
                      data-testid="llm-profiles-restore"
                    >
                      {restoreBusy ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Restoring…
                        </>
                      ) : (
                        "Restore previous catalog"
                      )}
                    </Button>
                    <p className="text-xs text-red-700 dark:text-red-400">
                      Prefer this over editing the JSON: it recovers what was
                      actually configured here, not a generic starting point.
                    </p>
                  </div>
                ) : starter ? (
                  <p className="text-sm text-red-700 dark:text-red-400">
                    <strong>
                      No previous catalog to restore — this platform's catalog
                      has never been overwritten.
                    </strong>{" "}
                    The JSON below is the{" "}
                    <strong>starter TEMPLATE</strong> from{" "}
                    <code>GET /llm-profiles/starter</code> (the live built-in
                    tiers). Review and edit it before saving — it is a starting
                    point, not a recovered configuration.
                  </p>
                ) : (
                  <p className="text-sm text-red-700 dark:text-red-400">
                    Neither recovery source could be read, so there is nothing
                    to offer and the editor below is empty. Saving from here
                    means writing a catalog by hand.
                  </p>
                )}

                {/* An unreadable source is NOT an absent one. Without this,
                    a failed /previous read renders identically to "you have
                    no previous catalog" — and the operator would stop looking
                    for the undo that actually exists. */}
                {recoveryReadError && (
                  <p
                    className="text-xs text-red-800 dark:text-red-300 border-t border-red-300 dark:border-red-800 pt-2"
                    data-testid="llm-profiles-recovery-read-error"
                  >
                    ⚠ Could not read: {recoveryReadError}. This is a failed
                    read, <strong>not</strong> a confirmed absence — retry
                    before concluding the option does not exist.
                  </p>
                )}
              </div>
            )}

            {/* Pre-flight advisory from /starter, rendered BEFORE the operator
                clicks Save. This is the guardrail lexserver built for exactly
                this screen and that no consumer used — the 422 it predicts is
                otherwise discovered by hitting it. Only meaningful for the
                template path; a restore re-runs the same gates server-side. */}
            {!current && !previous && starter && starter.seeding.missingProviders.length > 0 && (
              <div
                className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950 p-2 text-sm"
                data-testid="llm-profiles-starter-seeding"
              >
                <div className="font-medium mb-1">
                  This template cannot be saved yet.
                </div>
                <p className="text-amber-700 dark:text-amber-400 mb-1">
                  It references providers with no seed credential, so{" "}
                  <code>Save catalog</code> will be refused until they are
                  seeded (or the tiers are edited to not need them):
                </p>
                <ul className="list-disc pl-5 text-amber-700 dark:text-amber-400">
                  {starter.seeding.missingProviders.map((p) => (
                    <li key={p}>
                      <code>{p}</code>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  ⚠ This describes <strong>the template</strong>, not this
                  platform. For what this installation has seeded, see the
                  seed-credentials section below.
                </p>
              </div>
            )}

            {/* Tiers that can silently re-decide when pi-ai's catalog moves.
                `message` is authored server-side as operator-facing and safe to
                surface verbatim — do not recompose it from the structured
                fields. High before moderate: the server already ranked them. */}
            {!current && !previous && starter && starter.driftRisks.length > 0 && (
              <div
                className="rounded border border-slate-300 dark:border-slate-700 p-2 text-sm"
                data-testid="llm-profiles-starter-drift"
              >
                <div className="font-medium mb-1">
                  Tiers in this template that can drift
                </div>
                <ul className="space-y-1">
                  {starter.driftRisks.map((r) => (
                    <li key={r.profile} className="text-muted">
                      <span
                        className={
                          r.severity === "high"
                            ? "text-amber-600 dark:text-amber-400 font-medium"
                            : "text-slate-600 dark:text-slate-400"
                        }
                      >
                        {r.severity}
                      </span>{" "}
                      <code>{r.profile}</code> — {r.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {current && (
              <div className="text-sm text-muted">
                Last set by <code>{current.setBy}</code> on{" "}
                {new Date(current.setAt).toLocaleString()}{" "}
                {current.lastValidatedOk ? (
                  <span className="inline-flex items-center gap-1 ml-2">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    last validated{" "}
                    {new Date(current.lastValidatedAt ?? current.setAt).toLocaleString()}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 ml-2">
                    <AlertTriangle className="h-3 w-3 text-amber-500" />
                    last validation failed
                  </span>
                )}
              </div>
            )}

            {/* ── CATALOG ADVISORIES ─────────────────────────────────────
                Observations about the STORED catalog (lexserver's
                GET .../advisories). Consumer half of handshake cp7s.

                🚨 `statement`/`consequence` render VERBATIM. lexserver
                regex-tests its own prose to keep it a CONSEQUENCE rather than a
                verdict, because `defaultChatProfile` has no provenance — the
                server cannot tell "never advanced" from "deliberately chosen".
                Do NOT add "⚠ Problem" / "Fix this" chrome, and do NOT add a
                fix-it affordance: that re-adds the judgement the server
                deliberately refuses to make, and spends the mechanism's
                credibility on its first false positive.

                ⚠ Emphasis comes from `advisoryEmphasis(code)` — a function of
                the CODE only. Never branch on the prose; styling read from text
                would silently re-verdict every future row lexserver adds.

                Subject is named explicitly ("the stored catalog's default")
                because the absent-catalog path renders a DIFFERENT advisory
                family (`driftRisks`, about a TEMPLATE that can re-decide
                itself). They never render together today, but that is a
                convenience, not a design. */}
            {(() => {
              const state = selectAdvisoryState({
                catalogPresent: current !== null,
                set: advisories,
              });
              if (state.kind === "hidden" || state.kind === "none") return null;

              return (
                <div
                  className="rounded border border-slate-300 dark:border-slate-700 p-2 text-sm space-y-2"
                  data-testid="llm-profiles-advisories"
                >
                  <div className="font-medium">
                    Observations about this catalog&apos;s default chat profile
                  </div>

                  {state.kind === "unreadable" && (
                    <p
                      className="text-amber-700 dark:text-amber-400"
                      data-testid="llm-profiles-advisories-unreadable"
                    >
                      Could not read the advisories for this catalog. This is a
                      failed read, <strong>not</strong> a clean result — the
                      catalog may or may not have observations.
                    </p>
                  )}

                  {state.kind === "unknown" && (
                    <p
                      className="text-amber-700 dark:text-amber-400"
                      data-testid="llm-profiles-advisories-unknown"
                    >
                      Advisories could not be computed: {state.reason}. This is
                      not a clean bill of health — nothing was inspected.
                    </p>
                  )}

                  {state.kind === "rows" && (
                    <ul className="space-y-2" data-testid="llm-profiles-advisories-rows">
                      {state.advisories.advisories.map((a) => (
                        <li
                          key={a.code}
                          className={
                            advisoryEmphasis(a.code) === "verdict"
                              ? "border-l-2 border-amber-500 pl-2"
                              : "border-l-2 border-slate-300 dark:border-slate-700 pl-2"
                          }
                        >
                          <div className="text-slate-800 dark:text-slate-200">
                            {a.statement}
                          </div>
                          <div className="text-muted mt-0.5">{a.consequence}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}

            {current && (
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor="default-chat-profile">Default chat profile:</label>
                <select
                  id="default-chat-profile"
                  className="rounded border px-2 py-1 bg-white dark:bg-slate-900"
                  value={current.defaultChatProfile}
                  onChange={(e) => handleSetDefault(e.target.value)}
                  disabled={defaultBusy}
                >
                  {profileNames.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                {defaultBusy && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
            )}

            <div>
              <label
                htmlFor="catalog-json"
                className="text-sm font-medium block mb-1"
              >
                {current
                  ? "Catalog (JSON)"
                  : previous
                    ? "Previous catalog (JSON) — not yet restored"
                    : "Starter TEMPLATE (JSON) — not this platform's data"}
              </label>
              <textarea
                id="catalog-json"
                className="w-full rounded border px-2 py-1 text-xs font-mono bg-white dark:bg-slate-900"
                rows={20}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                data-testid="llm-profiles-json"
              />
              <p className="text-xs text-muted mt-1">
                Schema: <code>PlatformLlmProfilesSchema</code> from
                <code>@lexiun/lx-llm-resolver</code>. Pin-mode profiles
                short-circuit the resolver walk; criteria-mode profiles
                rank pi-ai's catalog at runtime.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Button onClick={handleSave} disabled={busy}>
                {busy ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    Saving…
                  </>
                ) : (
                  "Save catalog"
                )}
              </Button>
              {toast && (
                <span
                  className={`text-sm ${
                    toast.ok
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-red-700 dark:text-red-400"
                  }`}
                >
                  {toast.text}
                </span>
              )}
            </div>

            {/* A stale-draft refusal. Named separately from an ordinary failure
                because the operator's next question is always "who changed it,
                and when" — a bare 412 answers neither, which is why lexserver
                puts them in the body. The editor has already refreshed to the
                server's version, so what is on screen is now theirs, not the
                draft that was refused. */}
            {toast?.conflict && (
              <div
                className="rounded border border-amber-400 bg-amber-50 dark:bg-amber-950 p-2 text-sm"
                data-testid="llm-profiles-conflict"
              >
                <div className="font-medium mb-1">
                  Not saved — the catalog changed while this page was open.
                </div>
                <p className="text-amber-700 dark:text-amber-400">
                  {toast.conflict.storedSetBy ? (
                    <>
                      It was last saved by <code>{toast.conflict.storedSetBy}</code>
                      {toast.conflict.storedSetAt && (
                        <> on {new Date(toast.conflict.storedSetAt).toLocaleString()}</>
                      )}
                      .
                    </>
                  ) : (
                    <>The stored version could not be attributed.</>
                  )}{" "}
                  <strong>Nothing was written.</strong> The editor now shows
                  their version — re-apply your change on top of it if you still
                  want it.
                </p>
              </div>
            )}

            {toast?.missingProviders && toast.missingProviders.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950 p-2 text-sm">
                <div className="font-medium mb-1">Missing seed credentials:</div>
                <ul className="list-disc pl-5 text-amber-700 dark:text-amber-400">
                  {toast.missingProviders.map((p) => (
                    <li key={p}>
                      <code>{p}</code> — seed it in the section below before
                      saving this catalog.
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </>
        )}
      </CardContent>
    </Card>
  );
}
